import React from "react";
import { ClipboardList, ExternalLink, FileCheck2, Route } from "lucide-react";
import { getCourseProcess } from "../../data/courseProcess";

const INK = "hsl(222.2, 47.4%, 11.2%)";
const BRAND = "hsl(210,100%,45%)";

const Step = ({ n, title, children, testid }) => (
  <li className="relative pl-11 pb-6 last:pb-0" data-testid={testid}>
    <span
      className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold text-white"
      style={{ backgroundColor: BRAND }}
    >
      {n}
    </span>
    <span className="absolute left-4 top-9 bottom-0 w-px bg-slate-200 last:hidden" aria-hidden />
    <h3 className="text-base font-semibold text-slate-900">{title}</h3>
    <p className="mt-1 text-slate-600 leading-relaxed text-[15px]">{children}</p>
  </li>
);

const SourceLink = ({ source }) => (
  <a
    href={source.url}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 hover:underline"
    style={{ color: BRAND }}
  >
    {source.label} <ExternalLink className="h-3.5 w-3.5" />
  </a>
);

/**
 * Masterskabelonen for de forløb, der ikke har en myndighedsfastsat vognmandsprøve:
 * forløbet trin for trin, kravene efter kurset og de kilder, det hele står på.
 */
export const CourseProcessSection = ({ group }) => {
  const process = getCourseProcess(group.slug);
  if (!process) return null;

  return (
    <>
      <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="course-process">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" style={{ color: INK }}>
          <Route className="h-5 w-5" /> {process.processTitle}
        </h2>
        <ol className="mt-2">
          {process.steps.map((s, i) => (
            <Step key={s.title} n={i + 1} title={s.title} testid={`process-step-${i + 1}`}>
              {s.text}
            </Step>
          ))}
        </ol>
      </div>

      {process.requirements?.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="course-after-requirements">
          <h2 className="text-xl font-semibold mb-2 flex items-center gap-2" style={{ color: INK }}>
            <FileCheck2 className="h-5 w-5" /> {process.afterTitle}
          </h2>
          <p className="text-slate-600 leading-relaxed">{process.afterIntro}</p>

          <dl className="mt-4 space-y-3">
            {process.requirements.map((r, i) => (
              <div key={r.title} className="rounded-lg border border-slate-200 p-4" data-testid={`after-requirement-${i}`}>
                <dt className="flex items-center gap-2 font-semibold text-slate-900">
                  <ClipboardList className="h-4 w-4 shrink-0" style={{ color: BRAND }} />
                  {r.title}
                </dt>
                <dd className="mt-1 text-[15px] text-slate-600 leading-relaxed">{r.text}</dd>
              </div>
            ))}
          </dl>

          {process.notes?.length > 0 && (
            <ul className="mt-4 space-y-1.5 text-[15px] text-slate-600">
              {process.notes.map((n) => (
                <li key={n.label}>
                  <span className="text-slate-500">{n.label}:</span> {n.text}
                </li>
              ))}
            </ul>
          )}

          {process.sources?.length > 0 && (
            <p className="mt-4 text-sm text-slate-500" data-testid="process-sources">
              Kilder:{" "}
              {process.sources.map((s, i) => (
                <React.Fragment key={s.url}>
                  {i > 0 && " · "}
                  <SourceLink source={s} />
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      )}
    </>
  );
};

export default CourseProcessSection;
